import { createContext, useEffect, useReducer, useRef } from "react";
import PropTypes from "prop-types";
// utils
import axios from "../utils/axios";
import { isValidToken, setSession } from "../utils/jwt";
import queryClient from "../utils/queryClient";
import jwtDecode from "jwt-decode";
import { HOST_API } from "../config-prod";

// ----------------------------------------------------------------------

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
    };
  },
  LOGIN: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null,
  }),
  REGISTER: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
};

const reducer = (state, action) =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

const AuthContext = createContext({
  ...initialState,
  method: "jwt",
  login: () => Promise.resolve(),
  loginWithOTP: () => Promise.resolve(),
  requestLoginOTP: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  logoutAllDevices: () => Promise.resolve(),
  register: () => Promise.resolve(),
});

// ----------------------------------------------------------------------

AuthProvider.propTypes = {
  children: PropTypes.node,
};

// Get device information
const getDeviceInfo = () => ({
  deviceId: localStorage.getItem("deviceId") || `web-${Date.now()}`,
  deviceName: navigator.userAgent,
  deviceType: "WEB",
  appVersion: "1.0.0",
});

function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const refreshTimerRef = useRef(null);

  // Schedule a proactive token refresh before it expires
  const scheduleTokenRefresh = (accessToken) => {
    // Clear any existing timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    try {
      const decoded = jwtDecode(accessToken);
      const currentTime = Date.now() / 1000;
      const timeUntilExpiry = decoded.exp - currentTime;

      // Refresh 2 minutes before expiry (or immediately if less than 2 min left)
      const refreshIn = Math.max((timeUntilExpiry - 120) * 1000, 0);

      // console.log(`Token expires in ${Math.round(timeUntilExpiry / 60)} min, scheduling refresh in ${Math.round(refreshIn / 1000 / 60)} min`);

      refreshTimerRef.current = setTimeout(async () => {
        try {
          const refreshToken = localStorage.getItem("refreshToken");
          if (!refreshToken) return;

          // console.log("Proactively refreshing token...");
          const response = await axios.post(`${HOST_API}/users/auth/refresh`, { refreshToken });

          const data = response.data.data || response.data;
          const { accessToken: newAccessToken, refreshToken: newRefreshToken } = data;

          if (newAccessToken) {
            localStorage.setItem("accessToken", newAccessToken);
            if (newRefreshToken) {
              localStorage.setItem("refreshToken", newRefreshToken);
            }
            setSession(newAccessToken);
            scheduleTokenRefresh(newAccessToken);
            // console.log("Token proactively refreshed successfully");
          }
        } catch (error) {
          console.error("Proactive token refresh failed:", error);
          localStorage.removeItem("accessToken");
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("userType");
          localStorage.removeItem("companyId");
          setSession(null);
          queryClient.clear();
          dispatch({ type: "LOGOUT" });
          window.location.href = "/auth/login";
        }
      }, refreshIn);
    } catch (error) {
      console.error("Failed to schedule token refresh:", error);
    }
  };

  // Try to refresh the token using refreshToken
  const tryRefreshToken = async () => {
    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken) return null;

    try {
      // console.log("Attempting to refresh expired token...");
      const response = await axios.post(`${HOST_API}/users/auth/refresh`, { refreshToken });

      const data = response.data.data || response.data;
      const { accessToken: newAccessToken, refreshToken: newRefreshToken } = data;

      if (newAccessToken) {
        localStorage.setItem("accessToken", newAccessToken);
        if (newRefreshToken) {
          localStorage.setItem("refreshToken", newRefreshToken);
        }
        setSession(newAccessToken);
        // console.log("Token refreshed successfully during initialization");
        return newAccessToken;
      }
    } catch (error) {
      console.error("Token refresh failed during initialization:", error);
    }
    return null;
  };

  useEffect(() => {
    const initialize = async () => {
      try {
        // console.log("=== Initialization Started ===");

        let accessToken = localStorage.getItem("accessToken");
        const refreshToken = localStorage.getItem("refreshToken");
        // console.log("User Type from localStorage:", localStorage.getItem("userType"));
        // console.log("Is token valid?", isValidToken(accessToken));

        // If access token is expired but refresh token exists, try to refresh
        if ((!accessToken || !isValidToken(accessToken)) && refreshToken) {
          // console.log("Access token expired, attempting refresh...");
          accessToken = await tryRefreshToken();
        }

        if (accessToken && isValidToken(accessToken)) {
          // console.log("Token is valid, setting session...");
          setSession(accessToken);

          // Schedule proactive refresh
          scheduleTokenRefresh(accessToken);

          // console.log("Fetching user profile...");
          const response = await axios.get("/users/profile");

          // console.log("Profile response:", response.data);
// 
          const user =
            response.data.data || response.data.user || response.data;

          dispatch({
            type: "INITIALIZE",
            payload: {
              isAuthenticated: true,
              user,
            },
          });

          // console.log("=== Initialization Success ===");
        } else {
          // console.log("Token invalid or missing, clearing storage...");
          localStorage.removeItem("accessToken");
          localStorage.removeItem("refreshToken");
          setSession(null);

          dispatch({
            type: "INITIALIZE",
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });

          // console.log("=== Initialization Failed: No valid token ===");
        }
      } catch (err) {
        console.error("=== Initialize error ===", err);

        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        setSession(null);

        dispatch({
          type: "INITIALIZE",
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    };

    initialize();

    // Cleanup timer on unmount
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, []);

  // Login with mobile and password
  // Find the login function and update it to ensure proper token storage:

  const login = async (mobile, password) => {
    try {
      // Clear any existing tokens before login
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("userType");

      const deviceInfo = getDeviceInfo();

      const response = await axios.post("/users/auth/login/password", {
        mobile,
        password,
        ...deviceInfo,
      });

      // console.log("Full Login response:", response); // Debug full response
      // console.log("Login response.data:", response.data); // Debug log

      // Your backend likely returns tokens in a nested structure
      // Let's handle multiple possible response formats:
      let accessToken, refreshToken, user;

      if (response.data.data) {
        // Format 1: { data: { tokens: { accessToken, refreshToken }, user: {...} } }
        if (response.data.data.tokens) {
          accessToken = response.data.data.tokens.accessToken;
          refreshToken = response.data.data.tokens.refreshToken;
          user = response.data.data.user;
        }
        // Format 2: { data: { accessToken, refreshToken, user: {...} } }
        else {
          accessToken = response.data.data.accessToken;
          refreshToken = response.data.data.refreshToken;
          user = response.data.data.user || response.data.data;
        }
      }
      // Format 3: { accessToken, refreshToken, user: {...} }
      else {
        accessToken = response.data.accessToken;
        refreshToken = response.data.refreshToken;
        user = response.data.user;
      }

      // Store user type for role-based access
      if (user && user.role) {
        localStorage.setItem("userType", user.role);
      }

      // console.log("Extracted accessToken:", accessToken);
      // console.log("Extracted refreshToken:", refreshToken);
      // console.log("Extracted user:", user);

      // Verify tokens exist
      if (!accessToken || !refreshToken) {
        console.error(
          "Token extraction failed. Response structure:",
          response.data,
        );
        throw new Error("Tokens not received from server");
      }

      // console.log("Storing accessToken:", accessToken); // Debug log
      // console.log("Storing refreshToken:", refreshToken); // Debug log

      // Store tokens in localStorage
      localStorage.setItem("accessToken", accessToken);
      localStorage.setItem("refreshToken", refreshToken);
      localStorage.setItem("userType", user.role);
      if (user.companyId) {
        localStorage.setItem("companyId", user.companyId);
      }

      // Set session
      setSession(accessToken);

      // Schedule proactive token refresh
      scheduleTokenRefresh(accessToken);

      // Store device ID for future requests
      if (!localStorage.getItem("deviceId")) {
        localStorage.setItem("deviceId", deviceInfo.deviceId);
      }

      // Clear all cached queries so pages re-fetch with fresh user context
      queryClient.clear();

      dispatch({
        type: "LOGIN",
        payload: {
          user,
        },
      });
    } catch (error) {
      console.error("Login error:", error);
      console.error("Error response:", error.response?.data);
      throw error;
    }
  };
  // Request OTP for login (mobile or email)
  const requestLoginOTP = async ({ mobile, email }) => {
    const response = await axios.post("/users/auth/login/otp/request", {
      ...(email ? { email } : { mobile }),
    });

    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to send OTP");
    }

    return response.data;
  };

  // Login with (mobile or email) and OTP
  const loginWithOTP = async ({ mobile, email, otp }) => {
    const deviceInfo = getDeviceInfo();

    const response = await axios.post("/users/auth/login/otp/verify", {
      ...(email ? { email } : { mobile }),
      otp,
      ...deviceInfo,
    });

    let accessToken, refreshToken, user;

    if (response.data.data) {
      if (response.data.data.tokens) {
        accessToken = response.data.data.tokens.accessToken;
        refreshToken = response.data.data.tokens.refreshToken;
        user = response.data.data.user;
      } else {
        accessToken = response.data.data.accessToken;
        refreshToken = response.data.data.refreshToken;
        user = response.data.data.user || response.data.data;
      }
    } else {
      accessToken = response.data.accessToken;
      refreshToken = response.data.refreshToken;
      user = response.data.user;
    }

    if (!accessToken || !refreshToken) {
      throw new Error(response.data?.message || "OTP verification failed");
    }

    // Store tokens
    localStorage.setItem("accessToken", accessToken);
    localStorage.setItem("refreshToken", refreshToken);
    localStorage.setItem("userType", user.role);
    if (user.companyId) {
      localStorage.setItem("companyId", user.companyId);
    }
    if (user.clientId) {
      localStorage.setItem("clientId", user.clientId);
    }

    setSession(accessToken);

    // Schedule proactive token refresh
    scheduleTokenRefresh(accessToken);

    // Store device ID for future requests
    if (!localStorage.getItem("deviceId")) {
      localStorage.setItem("deviceId", deviceInfo.deviceId);
    }

    // Clear all cached queries so pages re-fetch with fresh user context
    queryClient.clear();

    dispatch({
      type: "LOGIN",
      payload: {
        user,
      },
    });
  };

  const register = async (email, password, firstName, lastName) => {
    // Keep your existing register logic or update as needed
    const response = await axios.post("/users", {
      email,
      password,
      firstName,
      lastName,
    });
    const { accessToken, user } = response.data;

    localStorage.setItem("accessToken", accessToken);
    localStorage.setItem("userType", user.role); // Store user type for role-based access
    setSession(accessToken);

    dispatch({
      type: "REGISTER",
      payload: {
        user,
      },
    });
  };

  const logout = async () => {
    const refreshToken = localStorage.getItem("refreshToken");

    // Clear refresh timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    try {
      // Call logout  to revoke refresh token
      if (refreshToken) {
        await axios.post("/users/auth/logout", { refreshToken });
      }
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      // Clear tokens regardless of API response
      setSession(null);
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("userType");
      localStorage.removeItem("companyId");
      queryClient.clear();
      dispatch({ type: "LOGOUT" });
    }
  };

  // Logout from all devices
  const logoutAllDevices = async () => {
    try {
      // Call logout all API (requires access token in header)
      await axios.post("/users/auth/logout-all");
    } catch (error) {
      console.error("Logout all devices error:", error);
    } finally {
      // Clear tokens regardless of API response
      setSession(null);
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("userType");
      localStorage.removeItem("companyId");
      queryClient.clear();
      dispatch({ type: "LOGOUT" });
    }
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: "jwt",
        login,
        loginWithOTP,
        requestLoginOTP,
        logout,
        logoutAllDevices,
        register,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
